import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta, { breadcrumbJsonLd } from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import EntityShareCard from "@/components/EntityShareCard";
import { ArrowRight, FlagBanner, MapPin, SealCheck, Warning } from "@phosphor-icons/react";

/** Permanent catalogue entity page — species, habitat, phenomenon,
 * water-body-type or landform. */
export default function CatalogueEntity() {
  const { hub: hubSlug, slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    http.get(`/catalogue/${hubSlug}/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setMissing(true));
  }, [hubSlug, slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const e = data?.entity;
  const hub = data?.hub;

  const jsonLd = e ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: e.title,
    description: e.summary,
    url: `${origin}/explore/${hubSlug}/${e.slug}`,
    mainEntity: e.scientific_name ? { "@type": "Thing", name: e.title, alternateName: e.scientific_name } : { "@type": "Thing", name: e.title },
    author: { "@type": "Organization", name: "NaturePulse" },
    isBasedOn: (e.sources || []).map((s) => s.url).filter(Boolean),
  } : null;

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="catalogue-entity">
      {e && (
        <SeoMeta
          title={`${e.title}${e.scientific_name ? ` (${e.scientific_name})` : ""} — ${hub.label}`}
          description={e.summary.slice(0, 155)}
          canonical={`${origin}/explore/${hubSlug}/${e.slug}`}
          ogType="article"
          jsonLd={[
            jsonLd,
            breadcrumbJsonLd([
              { name: "Home", url: origin },
              { name: "Catalogue", url: `${origin}/explore` },
              { name: hub.label, url: `${origin}/explore/${hub.slug}` },
              { name: e.title, url: `${origin}/explore/${hubSlug}/${e.slug}` },
            ]),
          ]}
        />
      )}
      <div className="max-w-[860px] mx-auto px-5 md:px-8">
        {missing ? (
          <h1 className="font-serif-display text-3xl" data-testid="entity-missing">Page not found.</h1>
        ) : !e ? (
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
        ) : (
          <>
            <Breadcrumbs items={[
              { label: "Home", to: "/" },
              { label: "Catalogue", to: "/explore" },
              { label: hub.label, to: `/explore/${hub.slug}` },
              { label: e.title },
            ]} />

            <div className="mt-4 flex flex-wrap items-center gap-2 font-mono-tech text-[9px] tracking-[0.2em] uppercase">
              <span className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-2.5 py-1">{e.kind?.replace(/-/g, " ")}</span>
              {e.conservation_status && (
                <span className="rounded-full border border-[#7C2D12]/30 text-[#7C2D12] px-2.5 py-1" data-testid="entity-conservation">
                  {e.conservation_status}
                </span>
              )}
              <span className="rounded-full border border-[#0C4A6E]/30 text-[#0C4A6E] px-2.5 py-1 inline-flex items-center gap-1">
                <SealCheck size={10} weight="fill" /> Reference page · sources below
              </span>
            </div>

            <h1 className="mt-4 font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight" data-testid="entity-title">{e.title}</h1>
            {e.scientific_name && (
              <div className="mt-1.5 font-serif-display italic text-xl text-[#3A3830]/85" data-testid="entity-sci-name">{e.scientific_name}</div>
            )}

            <p className="mt-4 text-base md:text-lg leading-relaxed text-[#22301F]" data-testid="entity-summary">{e.summary}</p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <ListenTranslateBar text={`${e.title}. ${e.scientific_name || ""}. ${e.summary}`} testidPrefix="entity-tools" />
              <ShareButton title={e.title} text={e.summary} variant="chip" size="md" testid="entity-share" />
              <EntityShareCard hub={hubSlug} slug={e.slug} testid="entity-card-btn" />
            </div>

            {hub.safety_note && (
              <div className="mt-5 rounded-xl border border-[#7C2D12]/30 bg-[#7C2D12]/8 p-4 text-sm text-[#7C2D12] flex gap-2" data-testid="entity-safety">
                <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                <span>{hub.safety_note}</span>
              </div>
            )}

            {/* Key facts */}
            {e.key_facts?.length > 0 && (
              <div className="mt-8 rounded-2xl bg-white border border-black/10 overflow-hidden" data-testid="entity-facts">
                <div className="px-5 py-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase bg-[#1B4332] text-[#F5EBDC]">Key facts</div>
                <dl className="divide-y divide-black/5">
                  {e.key_facts.map((f) => (
                    <div key={f.label} className="px-5 py-2.5 grid grid-cols-[140px_1fr] gap-3 text-sm">
                      <dt className="font-mono-tech text-[10px] tracking-[0.14em] uppercase text-[#3A3830]/70 pt-0.5">{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Sections */}
            {e.sections?.map((s) => (
              <section key={s.heading} className="mt-8">
                <h2 className="font-serif-display text-2xl md:text-3xl">{s.heading}</h2>
                <div className="cms-article-body mt-2 text-[15px] text-[#22301F]" dangerouslySetInnerHTML={{ __html: s.html }} />
              </section>
            ))}

            {/* Community observations */}
            <section className="mt-10" data-testid="entity-observations">
              <h2 className="font-serif-display text-2xl md:text-3xl mb-3 flex items-center gap-2">
                <MapPin size={20} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Community observations
              </h2>
              {data.observations?.length === 0 ? (
                <p className="text-sm text-[#3A3830]">None logged yet — be the first to record this.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {data.observations.map((o) => (
                    <Link key={o.id} to={`/observations/${o.id}`} data-testid={`entity-obs-${o.id}`}
                      className="rounded-xl border border-black/10 bg-white p-4 hover:border-[#1B4332] transition-colors">
                      <div className="font-serif-display text-base leading-tight">{o.title || "Observation"}</div>
                      <div className="mt-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/60">{o.date}</div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Related */}
            {data.related?.length > 0 && (
              <section className="mt-10" data-testid="entity-related">
                <h2 className="font-serif-display text-2xl mb-3">More in {hub.label}</h2>
                <div className="grid gap-2 sm:grid-cols-2">
                  {data.related.map((r) => (
                    <Link key={r.slug} to={`/explore/${hub.slug}/${r.slug}`} data-testid={`entity-related-${r.slug}`}
                      className="group rounded-xl border border-black/10 bg-white px-4 py-3 hover:border-[#1B4332] transition-colors flex items-center justify-between gap-2">
                      <div>
                        <span className="font-serif-display text-base">{r.title}</span>
                        {r.scientific_name && <span className="ml-2 font-serif-display italic text-sm text-[#3A3830]/70">{r.scientific_name}</span>}
                      </div>
                      <ArrowRight size={12} weight="bold" className="text-[color:var(--np-brand,#B85F2A)] group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Sources */}
            {e.sources?.length > 0 && (
              <section className="mt-10 rounded-2xl border border-black/10 bg-white p-5" data-testid="entity-sources">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Sources &amp; provenance</div>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {e.sources.map((s) => (
                    <li key={s.url}>
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-[#0C4A6E] underline underline-offset-2 hover:opacity-80">{s.label}</a>
                    </li>
                  ))}
                  {e.wikipedia_url && !e.sources.some((s) => s.url === e.wikipedia_url) && (
                    <li><a href={e.wikipedia_url} target="_blank" rel="noreferrer" className="text-[#0C4A6E] underline underline-offset-2">Wikipedia</a></li>
                  )}
                </ul>
                <p className="mt-3 text-[11px] text-[#3A3830]/70 flex items-center gap-1.5">
                  <FlagBanner size={11} weight="fill" /> Reference content curated by the NaturePulse desk — community observations are shown separately above. See an error? <Link to="/contact" className="underline underline-offset-2">Report a correction</Link>.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
